import axios from 'axios';
import useTokenStore from '@store/token-store/token.store.ts';

export interface AuthorizedPhone {
  clientIp: string;
  chromecastUuid: string;
  chromecastIp: string;
  roomNumber: string;
  deviceId: string;
  port: number;
  deviceName: string;
}

export interface ChromecastDevice {
  id: string;
  name: string;
  ip: string;
  port: number;
  model?: string;
  manufacturer?: string;
  version?: string;
  capabilities?: string[];
  discoveryTime: string;
  lastSeen: string;
  assignedRoom?: string | null;
  isAssigned: boolean;
}

export interface RoomChromecast {
  roomNumber: string;
  device: ChromecastDevice;
}

export interface PhoneToRoomConnection {
  phoneIp: string;
  phoneDeviceId: string;
  roomNumber: string;
  chromecastUuid: string;
  chromecastIp: string;
  chromecastName: string;
  proxyPort: number;
  isConnected: boolean;
}

export interface AuditLog {
  timestamp: string;
  type: 'query' | 'response';
  clientIp?: string;
  queryName?: string;
  queryType?: string;
  status?: string;
  message?: string;
  deviceName?: string;
}

export interface ChromecastAssignment {
  chromecastUuid: string;
  roomNumber: string;
  chromecastName?: string;
  chromecastModel?: string;
  lastKnownIp?: string;
  isActive: boolean;
  assignedAt?: string;
}

export interface CastAdminData {
  authorizedPhones: AuthorizedPhone[];
  chromecastsByRoom: RoomChromecast[];
  allChromecasts: ChromecastDevice[];
  phoneToRoomConnections: PhoneToRoomConnection[];
  auditLogs: AuditLog[];
  totalPhones: number;
  totalChromecasts: number;
  totalConnections: number;
  assignments: ChromecastAssignment[];
}

export interface CreateAssignmentData {
  chromecastUuid: string;
  roomNumber: string;
  chromecastName?: string;
  chromecastModel?: string;
  lastKnownIp?: string;
}

// Helper function to make HTTP requests via Electron or axios
const makeRequest = async <T>(
  baseUrl: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  data?: unknown
): Promise<T> => {
  const { accessToken } = useTokenStore.getState();
  const url = `${baseUrl}${path}`;

  if (window.electronAPI) {
    const response = await window.electronAPI.httpRequest({
      method,
      url,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      data: data ? JSON.stringify(data) : undefined
    });

    if (!response.ok) {
      throw new Error(response.error || `Request failed: ${method} ${path}`);
    }

    return response.data;
  } else {
    const response = await axios({
      method,
      url,
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      data
    });
    return response.data;
  }
};

export const getCastAdminData = async (
  baseUrl: string
): Promise<CastAdminData> => {
  return makeRequest<CastAdminData>(baseUrl, 'GET', '/api/admin/data');
};

export const getChromecastAssignments = async (
  baseUrl: string
): Promise<ChromecastAssignment[]> => {
  return makeRequest<ChromecastAssignment[]>(baseUrl, 'GET', '/api/assignments');
};

export const createChromecastAssignment = async (
  baseUrl: string,
  data: CreateAssignmentData
): Promise<ChromecastAssignment> => {
  return makeRequest<ChromecastAssignment>(
    baseUrl,
    'POST',
    '/api/assignments',
    data
  );
};

export const updateChromecastAssignment = async (
  baseUrl: string,
  chromecastUuid: string,
  data: Partial<CreateAssignmentData>
): Promise<ChromecastAssignment> => {
  return makeRequest<ChromecastAssignment>(
    baseUrl,
    'PUT',
    `/api/assignments/chromecast/${encodeURIComponent(chromecastUuid)}`,
    data
  );
};

export const deleteChromecastAssignment = async (
  baseUrl: string,
  chromecastUuid: string
): Promise<void> => {
  return makeRequest<void>(
    baseUrl,
    'DELETE',
    `/api/assignments/chromecast/${encodeURIComponent(chromecastUuid)}`
  );
};
